/**
 * Finance expenses service (migrations 2026-10-21-970..972).
 *
 * Every write is a SECURITY DEFINER RPC; the tables are SELECT-only for authenticated and RLS decides what each
 * person sees. Nothing here is an enforcement point: role, event access, status transitions, self-approval and
 * override reasons are all checked server-side. Errors come back as "FINANCE_CODE: human message";
 * financeErrorMessage() strips the code for display.
 */
import { supabase } from "./supabase";
import { PICKER_PAGE_SIZE } from "./pickerOptions.service";
import type { PickerPage } from "./pickerOptions.service";
import { postgrestIlikeTerm } from "../utils/postgrestSearch";
import type {
  ActualFields,
  ExportBatch,
  ExportBatchItem,
  ExportTemplate,
  ExportTemplateColumn,
  StateEntity,
  ApprovalRound,
  ApproverManager,
  BillingCategory,
  BudgetSummary,
  CapabilityGrant,
  Department,
  DocumentKind,
  Expense,
  ExpenseDefaults,
  ExpenseDocument,
  ExpenseFields,
  ExpenseHistoryEvent,
  ExpenseType,
  FinanceAccess,
  FinanceCapability,
  FinanceLookups,
  FinanceSetting,
  GlMapping,
  Payee,
  PaymentTerm,
  StaffPerson,
} from "../types/finance.types";

// The generated database types predate these tables/RPCs; keep the untyped surface here.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export const FINANCE_DOCUMENTS_BUCKET = "finance-documents";
export const FINANCE_DOCUMENT_MAX_BYTES = 26214400; // 25 MiB, mirrors the bucket
export const FINANCE_DOCUMENT_ACCEPT =
  ".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,.xls,.xlsx";
const ALLOWED_MIME = new Set([
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);

/** "FINANCE_FORBIDDEN: no finance access to this event" -> "No finance access to this event". */
export function financeErrorMessage(err: unknown): string {
  const raw =
    (err as { message?: string } | null)?.message ??
    (typeof err === "string" ? err : "Something went wrong");
  const m = /^FINANCE_[A-Z_]+:\s*(.*)$/s.exec(raw.trim());
  const text = m ? m[1] : raw;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

async function rpc<T>(
  fn: string,
  args: Record<string, unknown> = {},
): Promise<T> {
  const { data, error } = await db.rpc(fn, args);
  if (error) {
    throw error;
  }
  return data as T;
}

async function select<T>(
  table: string,
  build: (q: any) => any = (q) => q, // eslint-disable-line @typescript-eslint/no-explicit-any
): Promise<T[]> {
  const { data, error } = await build(db.from(table).select("*"));
  if (error) {
    throw error;
  }
  return (data ?? []) as T[];
}

// ---------------------------------------------------------------- access

/** Never throws: an error means "do not show finance". */
export async function getFinanceAccess(
  eventId?: string | null,
): Promise<FinanceAccess> {
  try {
    return await rpc<FinanceAccess>("finance_my_access", {
      p_event_id: eventId ?? null,
    });
  } catch {
    return {
      enabled: false,
      staff: false,
      capabilities: [],
      event_access: false,
    };
  }
}

// ---------------------------------------------------------------- lookups / config

/**
 * Small master data only. Payees are NOT here: there are ~9,800 vendors and PostgREST returns at most
 * 1,000 rows per request, so a load-everything list would silently drop most of them. Payees are
 * searched server-side (searchPayeeOptions / listPayeesPage) and named by id (getPayeesByIds).
 */
export async function getFinanceLookups(): Promise<FinanceLookups> {
  const [types, departments, categories, anyVendor, terms] = await Promise.all([
    select<ExpenseType>("finance_expense_types", (q) =>
      q.order("sort_order").order("name"),
    ),
    select<Department>("finance_departments", (q) => q.order("name")),
    select<BillingCategory>("finance_billing_categories", (q) =>
      q.order("name"),
    ),
    select<{ id: string }>("finance_payees", (q) =>
      q.select("id").eq("payee_type", "vendor").eq("is_active", true).limit(1),
    ),
    select<PaymentTerm>("finance_payment_terms", (q) => q.order("code")),
  ]);
  return {
    types,
    departments,
    categories,
    hasActiveVendors: anyVendor.length > 0,
    terms,
  };
}

// ---------------------------------------------------------------- payees (server-side search)

/** Columns a picker / name lookup needs (never the whole row). */
const PAYEE_PICK_COLUMNS = "id,display_name,bc_vendor_no,is_active,payee_type";
export type PayeeRef = Pick<
  Payee,
  "id" | "display_name" | "bc_vendor_no" | "is_active" | "payee_type"
>;

/** "Acme Catering · Vendor No. A01059" -- 120+ vendor names are shared, the number tells them apart. */
export const payeeLabelOf = (p: PayeeRef): string =>
  [
    `${p.display_name}${p.is_active ? "" : " (inactive)"}`,
    p.bc_vendor_no ? `Vendor No. ${p.bc_vendor_no}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

/** The `.or()` clause matching a vendor by name or BC vendor number (case-insensitive, contains). */
export const payeeSearchClause = (term: string): string | null => {
  const t = postgrestIlikeTerm(term);
  return t ? `display_name.ilike.*${t}*,bc_vendor_no.ilike.*${t}*` : null;
};

/**
 * One page of ACTIVE vendors for a picker (SearchableSelect), ordered by name then id; one extra row
 * is read to know whether another page exists ("Load more"). Finance RLS (finance_is_staff) decides
 * who gets any rows at all: MSRs and non-finance users get none.
 */
export const searchPayeeOptions = async (
  term: string,
  page = 0,
): Promise<PickerPage> => {
  const from = page * PICKER_PAGE_SIZE;
  let q = db
    .from("finance_payees")
    .select(PAYEE_PICK_COLUMNS)
    .eq("payee_type", "vendor")
    .eq("is_active", true)
    .order("display_name", { ascending: true })
    .order("id", { ascending: true })
    .range(from, from + PICKER_PAGE_SIZE); // PAGE_SIZE + 1 rows
  const clause = payeeSearchClause(term);
  if (clause) {
    q = q.or(clause);
  }
  const { data, error } = await q;
  if (error) {
    throw error;
  }
  const rows = (data ?? []) as PayeeRef[];
  return {
    options: rows
      .slice(0, PICKER_PAGE_SIZE)
      .map((p) => ({ value: p.id, label: payeeLabelOf(p) })),
    hasMore: rows.length > PICKER_PAGE_SIZE,
  };
};

/** Label of the current value of an expense (inactive payees included, so an old link still shows). */
export const payeeLabel = async (id: string): Promise<string | null> => {
  const rows = await getPayeesByIds([id]);
  return rows[0] ? payeeLabelOf(rows[0]) : null;
};

const PAYEE_ID_CHUNK = 100;
/** Just the payees these ids point at (chunked so the URL stays short; never the whole table). */
export async function getPayeesByIds(ids: string[]): Promise<PayeeRef[]> {
  const unique = Array.from(new Set(ids.filter(Boolean))).sort();
  const out: PayeeRef[] = [];
  for (let i = 0; i < unique.length; i += PAYEE_ID_CHUNK) {
    const { data, error } = await db
      .from("finance_payees")
      .select(PAYEE_PICK_COLUMNS)
      .in("id", unique.slice(i, i + PAYEE_ID_CHUNK));
    if (error) {
      throw error;
    }
    out.push(...((data ?? []) as PayeeRef[]));
  }
  return out;
}

export const PAYEE_ADMIN_PAGE_SIZE = 50;
/**
 * Finance settings: one page of ALL payees (active and inactive) matching the term, with the total
 * match count, so every one of the ~9,800 vendors is reachable by search or by paging.
 */
export async function listPayeesPage(
  term: string,
  page: number,
): Promise<{ rows: Payee[]; total: number }> {
  const from = page * PAYEE_ADMIN_PAGE_SIZE;
  let q = db
    .from("finance_payees")
    .select("*", { count: "exact" })
    .order("display_name", { ascending: true })
    .order("id", { ascending: true })
    .range(from, from + PAYEE_ADMIN_PAGE_SIZE - 1);
  const clause = payeeSearchClause(term);
  if (clause) {
    q = q.or(clause);
  }
  const { data, error, count } = await q;
  if (error) {
    throw error;
  }
  return { rows: (data ?? []) as Payee[], total: count ?? 0 };
}

export const listGlMappings = () =>
  select<GlMapping>("finance_gl_mappings", (q) => q.order("gl_account_no"));
export const listSettings = () =>
  select<FinanceSetting>("finance_settings", (q) => q.order("key"));
export const listApproverManagers = () =>
  select<ApproverManager>("finance_approver_managers");
export const listStaffDirectory = () =>
  rpc<StaffPerson[]>("finance_staff_directory");
export const listCapabilityGrants = () =>
  rpc<CapabilityGrant[]>("finance_admin_list_capabilities");

export const setSetting = (key: string, value: unknown) =>
  rpc("finance_admin_set_setting", { p_key: key, p_value: value });
export const setCapability = (
  profileId: string,
  capability: FinanceCapability,
  granted: boolean,
) =>
  rpc("finance_admin_set_capability", {
    p_profile_id: profileId,
    p_capability: capability,
    p_granted: granted,
  });
export const upsertExpenseType = (
  t: Omit<ExpenseType, "sort_order"> & { sort_order?: number },
) =>
  rpc("finance_admin_upsert_expense_type", {
    p_code: t.code,
    p_name: t.name,
    p_is_active: t.is_active,
    p_sort_order: t.sort_order ?? 100,
  });
export const upsertDepartment = (
  d: Partial<Department> & { code: string; name: string },
) =>
  rpc("finance_admin_upsert_department", {
    p_id: d.id ?? null,
    p_code: d.code,
    p_name: d.name,
    p_default_approver_profile_id: d.default_approver_profile_id ?? null,
    p_bc_dimension_code: d.bc_dimension_code ?? null,
    p_is_active: d.is_active ?? true,
  });
export const upsertBillingCategory = (
  c: Partial<BillingCategory> & { code: string; name: string },
) =>
  rpc("finance_admin_upsert_billing_category", {
    p_id: c.id ?? null,
    p_code: c.code,
    p_name: c.name,
    p_is_active: c.is_active ?? true,
  });
export const upsertGlMapping = (
  m: Partial<GlMapping> & {
    department_id: string;
    billing_category_id: string;
    gl_account_no: string;
  },
) =>
  rpc("finance_admin_upsert_gl_mapping", {
    p_id: m.id ?? null,
    p_department_id: m.department_id,
    p_billing_category_id: m.billing_category_id,
    p_expense_type_code: m.expense_type_code ?? null,
    p_gl_account_no: m.gl_account_no,
    p_is_active: m.is_active ?? true,
  });
export const upsertPaymentTerm = (t: PaymentTerm) =>
  rpc("finance_admin_upsert_payment_term", {
    p_code: t.code,
    p_name: t.name,
    p_net_days: t.net_days,
    p_reference_basis: t.reference_basis,
    p_is_active: t.is_active,
  });
export const upsertPayee = (
  p: Partial<Payee> & { payee_type: Payee["payee_type"]; display_name: string },
) =>
  rpc("finance_admin_upsert_payee", {
    p_id: p.id ?? null,
    p_payee_type: p.payee_type,
    p_display_name: p.display_name,
    p_bc_vendor_no: p.bc_vendor_no ?? null,
    p_organization_id: p.organization_id ?? null,
    p_profile_id: p.profile_id ?? null,
    p_default_department_id: p.default_department_id ?? null,
    p_payment_terms_code: p.payment_terms_code ?? null,
    p_state: p.state ?? null,
    p_email: p.email ?? null,
    p_is_active: p.is_active ?? true,
  });
export const importPayees = (rows: Record<string, string>[]) =>
  rpc<{ inserted: number; updated: number; rows: number }>(
    "finance_admin_import_payees",
    {
      p_rows: rows,
    },
  );
export const setApproverManager = (
  profileId: string,
  managerId: string | null,
) =>
  rpc("finance_admin_set_approver_manager", {
    p_profile_id: profileId,
    p_manager_profile_id: managerId,
  });

/** Minimal CSV parser (quoted fields, embedded commas/quotes/newlines). Header row -> lower_snake keys. */
export function parseCsv(text: string): Record<string, string>[] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          quoted = false;
        }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ",") {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") {
        i++;
      }
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += ch;
    }
  }
  if (field !== "" || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  const nonEmpty = rows.filter((r) => r.some((c) => c.trim() !== ""));
  if (nonEmpty.length === 0) {
    return [];
  }
  const header = nonEmpty[0].map((h) =>
    h
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, ""),
  );
  return nonEmpty.slice(1).map((r) => {
    const o: Record<string, string> = {};
    header.forEach((h, idx) => {
      if (h) {
        o[h] = (r[idx] ?? "").trim();
      }
    });
    return o;
  });
}

// ---------------------------------------------------------------- budget + expenses

export const setEventBudget = (
  eventId: string,
  total: number,
  reason?: string,
) =>
  rpc("finance_set_event_budget", {
    p_event_id: eventId,
    p_total_budget: total,
    p_reason: reason ?? null,
  });
export const getBudgetSummary = (eventId: string) =>
  rpc<BudgetSummary>("finance_event_budget_summary", { p_event_id: eventId });

export const listEventExpenses = (eventId: string) =>
  select<Expense>("finance_expenses", (q) =>
    q
      .eq("event_id", eventId)
      .order("created_at", { ascending: false })
      .limit(1000),
  );

export interface ExpenseFilter {
  status?: string;
  search?: string;
  unlinkedOnly?: boolean;
}

export const listExpenses = (f: ExpenseFilter = {}) =>
  select<Expense>("finance_expenses", (q) => {
    let x = q.order("created_at", { ascending: false }).limit(1000);
    if (f.status) {
      x = x.eq("status", f.status);
    }
    if (f.unlinkedOnly) {
      x = x.is("event_id", null);
    }
    const s = (f.search ?? "").trim();
    if (s) {
      // PostgREST or(): strip the characters that would break the filter grammar.
      const safe = s.replace(/[,()*%\\]/g, " ").trim();
      if (safe) {
        x = x.or(`expense_no.ilike.*${safe}*,description.ilike.*${safe}*`);
      }
    }
    return x;
  });

export async function getExpense(id: string): Promise<Expense | null> {
  const rows = await select<Expense>("finance_expenses", (q) =>
    q.eq("id", id).limit(1),
  );
  return rows[0] ?? null;
}

export const getExpenseDefaults = (a: {
  eventId: string | null;
  payeeId: string | null;
  departmentId: string | null;
  categoryId: string | null;
  typeCode: string | null;
  serviceDate: string | null;
}) =>
  rpc<ExpenseDefaults>("finance_expense_defaults", {
    p_event_id: a.eventId,
    p_payee_id: a.payeeId,
    p_department_id: a.departmentId,
    p_billing_category_id: a.categoryId,
    p_expense_type_code: a.typeCode,
    p_service_date: a.serviceDate,
  });

export const saveExpense = (
  id: string | null,
  rowVersion: number | null,
  fields: ExpenseFields,
) =>
  rpc<Expense>("finance_save_expense", {
    p_expense_id: id,
    p_row_version: rowVersion,
    p_fields: fields,
  });
export const cancelExpense = (e: Expense, reason: string) =>
  rpc<Expense>("finance_cancel_expense", {
    p_expense_id: e.id,
    p_row_version: e.row_version,
    p_reason: reason,
  });
export const submitExpense = (
  e: Expense,
  approverId: string | null,
  comment: string | null,
) =>
  rpc<Expense>("finance_submit_expense", {
    p_expense_id: e.id,
    p_row_version: e.row_version,
    p_approver_id: approverId,
    p_comment: comment,
  });
export const decideExpense = (
  e: Expense,
  decision: "approved" | "returned" | "rejected",
  comment: string | null,
) =>
  rpc<Expense>("finance_decide_expense", {
    p_expense_id: e.id,
    p_row_version: e.row_version,
    p_decision: decision,
    p_comment: comment,
  });
export const reassignApproval = (
  e: Expense,
  assigneeId: string,
  reason: string,
) =>
  rpc<Expense>("finance_reassign_approval", {
    p_expense_id: e.id,
    p_new_assignee: assigneeId,
    p_reason: reason,
  });
export const cloneExpense = (e: Expense) =>
  rpc<Expense>("finance_clone_expense", { p_expense_id: e.id });

export const listHistory = (expenseId: string) =>
  select<ExpenseHistoryEvent>("finance_expense_events", (q) =>
    q.eq("expense_id", expenseId).order("id", { ascending: true }),
  );
export const listApprovalRounds = (expenseId: string) =>
  select<ApprovalRound>("finance_expense_approvals", (q) =>
    q.eq("expense_id", expenseId).order("requested_at", { ascending: true }),
  );
export const listMyOpenApprovals = (profileId: string) =>
  select<ApprovalRound>("finance_expense_approvals", (q) =>
    q
      .eq("assignee_id", profileId)
      .is("decision", null)
      .order("requested_at", { ascending: true }),
  );
export const listExpensesByIds = (ids: string[]) =>
  ids.length === 0
    ? Promise.resolve([] as Expense[])
    : select<Expense>("finance_expenses", (q) => q.in("id", ids));

// ---------------------------------------------------------------- documents

export const listDocuments = (expenseId: string) =>
  select<ExpenseDocument>("finance_expense_documents", (q) =>
    q.eq("expense_id", expenseId).order("uploaded_at", { ascending: true }),
  );

export function validateDocument(file: File): string | null {
  if (!ALLOWED_MIME.has(file.type)) {
    return `"${file.name}" is not an allowed file type. Attach a PDF, image, Word or Excel file.`;
  }
  if (file.size <= 0) {
    return `"${file.name}" is empty.`;
  }
  if (file.size > FINANCE_DOCUMENT_MAX_BYTES) {
    return `"${file.name}" is over the 25 MB limit.`;
  }
  return null;
}

async function sha256Hex(file: File): Promise<string | null> {
  try {
    const buf = await file.arrayBuffer();
    const digest = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
}

/** Upload to expenses/<expense_id>/<uuid>-<safe name>, then register it (server re-checks access + reads size/mime). */
export async function uploadDocument(
  expenseId: string,
  file: File,
  kind: DocumentKind,
): Promise<ExpenseDocument> {
  const problem = validateDocument(file);
  if (problem) {
    throw new Error(problem);
  }
  const safe =
    file.name.replace(/[^A-Za-z0-9._-]+/g, "_").slice(-120) || "file";
  const path = `expenses/${expenseId}/${crypto.randomUUID()}-${safe}`;
  const { error: upErr } = await supabase.storage
    .from(FINANCE_DOCUMENTS_BUCKET)
    .upload(path, file, { contentType: file.type, upsert: false });
  if (upErr) {
    throw upErr;
  }
  return rpc<ExpenseDocument>("finance_register_document", {
    p_expense_id: expenseId,
    p_storage_path: path,
    p_file_name: file.name,
    p_kind: kind,
    p_sha256: await sha256Hex(file),
  });
}

export const removeDocument = (documentId: string, reason: string) =>
  rpc("finance_remove_document", {
    p_document_id: documentId,
    p_reason: reason,
  });

export async function documentUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from(FINANCE_DOCUMENTS_BUCKET)
    .createSignedUrl(path, 60);
  if (error || !data?.signedUrl) {
    throw error ?? new Error("Could not open the document");
  }
  return data.signedUrl;
}

// ---------------------------------------------------------------- display helpers

export const STATUS_LABEL: Record<string, string> = {
  draft: "Draft",
  pending_approval: "Pending approval",
  returned: "Returned for changes",
  rejected: "Rejected",
  approved: "Approved, awaiting invoice",
  cancelled: "Cancelled",
  actual_returned: "Actual returned for correction",
  actual_review_pending: "Actual awaiting approval",
  ready_for_export: "Ready for export",
  exported: "Exported to BC",
};

export const STATUS_CLASS: Record<string, string> = {
  draft: "bg-gray-100 text-gray-700",
  pending_approval: "bg-amber-100 text-amber-800",
  returned: "bg-orange-100 text-orange-800",
  rejected: "bg-red-100 text-red-700",
  approved: "bg-green-100 text-green-800",
  cancelled: "bg-gray-100 text-gray-500 line-through",
  actual_returned: "bg-orange-100 text-orange-800",
  actual_review_pending: "bg-amber-100 text-amber-800",
  ready_for_export: "bg-blue-100 text-blue-800",
  exported: "bg-indigo-100 text-indigo-800",
};

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});
export const money = (n: number | string | null | undefined): string =>
  n === null || n === undefined || n === "" ? "—" : usd.format(Number(n));

export const personName = (
  people: Map<string, StaffPerson>,
  id: string | null | undefined,
): string => {
  if (!id) {
    return "—";
  }
  const p = people.get(id);
  return p ? p.full_name || p.email : "Unknown user";
};

// ---------------------------------------------------------------- 1d-1f (migrations 975-978)

export const actualizeExpense = (e: Expense, fields: ActualFields) =>
  rpc<Expense>("finance_actualize_expense", {
    p_expense_id: e.id,
    p_row_version: e.row_version,
    p_fields: fields,
  });
export const overrideDecision = (
  e: Expense,
  decision: "approved" | "returned",
  reason: string,
) =>
  rpc<Expense>("finance_admin_override_decision", {
    p_expense_id: e.id,
    p_row_version: e.row_version,
    p_decision: decision,
    p_reason: reason,
  });
export const apReturnExpense = (e: Expense, reason: string) =>
  rpc<Expense>("finance_ap_return_expense", {
    p_expense_id: e.id,
    p_row_version: e.row_version,
    p_reason: reason,
  });
export const voidExportItem = (expenseId: string, reason: string) =>
  rpc<Expense>("finance_admin_void_export_item", {
    p_expense_id: expenseId,
    p_reason: reason,
  });
export const setDepartmentAlternate = (
  departmentId: string,
  profileId: string | null,
) =>
  rpc("finance_admin_set_department_alternate", {
    p_department_id: departmentId,
    p_profile_id: profileId,
  });
export const setTermEscalation = (code: string, days: number | null) =>
  rpc("finance_admin_set_term_escalation", { p_code: code, p_days: days });
export const setExpenseTypeInvoiceType = (
  code: string,
  invoiceType: string | null,
) =>
  rpc("finance_admin_set_expense_type_invoice_type", {
    p_code: code,
    p_bc_invoice_type: invoiceType,
  });
export const listStateEntities = () =>
  select<StateEntity>("finance_state_entities", (q) => q.order("state_code"));
export const upsertStateEntity = (e: StateEntity) =>
  rpc("finance_admin_upsert_state_entity", {
    p_state_code: e.state_code,
    p_entity_label: e.entity_label,
    p_is_active: e.is_active,
  });
export const listExportTemplates = () =>
  select<ExportTemplate>("finance_export_templates", (q) =>
    q.order("version", { ascending: false }),
  );
export const saveExportTemplate = (t: {
  name: string;
  columns: ExportTemplateColumn[];
  delimiter: string;
  include_header: boolean;
  date_format: string;
  line_ending: string;
  amount_format: string;
}) =>
  rpc<ExportTemplate>("finance_admin_save_export_template", {
    p_name: t.name,
    p_columns: t.columns,
    p_delimiter: t.delimiter,
    p_include_header: t.include_header,
    p_date_format: t.date_format,
    p_line_ending: t.line_ending,
    p_amount_format: t.amount_format,
  });
export const exportPreview = (ids: string[]) =>
  rpc<{
    csv: string;
    problems: Record<string, string[]>;
    template_version: number;
    note?: string;
  }>("finance_export_preview", { p_expense_ids: ids });
export const createExportBatch = (ids: string[], clientToken: string) =>
  rpc<{
    batch_id: string;
    batch_no: string;
    bc_batch_id: string;
    row_count: number;
    total_amount: number;
    file_name: string;
    file_sha256: string;
    replayed: boolean;
    bc_document_first?: string | null;
    bc_document_last?: string | null;
  }>("finance_create_export_batch", {
    p_expense_ids: ids,
    p_client_token: clientToken,
  });
export const listExportBatches = () =>
  select<ExportBatch>("finance_export_batches", (q) =>
    q
      .select(
        "id,batch_no,bc_batch_id,post_date,template_version,created_by,created_at,row_count,total_amount,file_name,file_sha256,finance_exported_invoices(bc_document_no)",
      )
      .order("created_at", { ascending: false })
      .limit(200),
  );
const BATCH_ITEM_COLUMNS =
  "id,batch_id,expense_id,line_no,voided_at,void_reason,bc_document_no," +
  "expense_no:row_snapshot->values->>expense_no,vendor_invoice_number:row_snapshot->values->>invoice_number";
export const listBatchItems = (batchId: string) =>
  select<ExportBatchItem>("finance_export_batch_items", (q) =>
    q.select(BATCH_ITEM_COLUMNS).eq("batch_id", batchId).order("line_no"),
  );
/** export lines whose CRM invoice number contains the (cleaned) search text -- finance admins only (RLS) */
export const searchBcDocument = (cleaned: string) =>
  select<ExportBatchItem>("finance_export_batch_items", (q) =>
    q
      .select(BATCH_ITEM_COLUMNS)
      .ilike("bc_document_no", `%${cleaned}%`)
      .order("bc_document_no")
      .limit(50),
  );
/** the export lines of one expense (current and voided), newest first -- finance admins only (RLS) */
export const listExpenseExportItems = (expenseId: string) =>
  select<ExportBatchItem>("finance_export_batch_items", (q) =>
    q
      .select(BATCH_ITEM_COLUMNS)
      .eq("expense_id", expenseId)
      .order("bc_document_no", { ascending: false, nullsFirst: false }),
  );
export async function downloadBatch(batchId: string): Promise<void> {
  const { data, error } = await db
    .from("finance_export_batches")
    .select("csv_text,file_name")
    .eq("id", batchId)
    .single();
  if (error) {
    throw error;
  }
  const blob = new Blob([data.csv_text], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = data.file_name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}
export const listFinanceAdminQueue = () =>
  select<ApprovalRound>("finance_expense_approvals", (q) =>
    q
      .is("decision", null)
      .eq("escalation_target", "finance_admin_queue")
      .order("requested_at"),
  );
export const listMyOpenApprovalsAny = (profileId: string) =>
  select<ApprovalRound>("finance_expense_approvals", (q) =>
    q
      .is("decision", null)
      .or(
        `assignee_id.eq.${profileId},alternate_assignee_id.eq.${profileId},original_assignee_id.eq.${profileId}`,
      )
      .order("requested_at", { ascending: true }),
  );
